import { Box3, Object3D, Vector3 } from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { EngineContext } from './engine-context';
import { OrbitPivotManager } from './orbit-pivot.manager';

/** The editor groups that hold modelled bodies, as opposed to helpers and overlays. */
export function contentRoots(ctx: Pick<EngineContext, 'group' | 'scene'>): Object3D[] {
  // engine.group only holds project parts (assembly STEP files). Bodies
  // created in the editor (manual "Add Shape" tools and the AI agent's
  // cad_script_runner) live in CADEditorGroup, which is a sibling under
  // engine.scene rather than a child of engine.group.
  const roots: Object3D[] = [ctx.group];
  for (const child of ctx.scene.children) {
    if (child === ctx.group) continue;
    if (child.name === 'CADEditorGroup' || child.userData?.['isCADGroup']) roots.push(child);
  }
  return roots;
}

/** World bounds of the project parts and the bodies made in the editor. */
export function contentBounds(ctx: Pick<EngineContext, 'group' | 'scene'>): Box3 {
  const box = new Box3();
  for (const root of contentRoots(ctx)) {
    const sub = new Box3().setFromObject(root);
    if (!sub.isEmpty()) box.union(sub);
  }
  return box;
}

/**
 * Manages keyboard shortcuts (F = focus, H = home, etc.) and the
 * camera operations they invoke. Extracted from Engine to reduce class size.
 */
export class KeyboardShortcutsManager {
  private handler?: (event: KeyboardEvent) => void;
  private homeViewState?: {
    position: Vector3;
    target: Vector3;
    up: Vector3;
    zoom: number;
  };

  constructor(
    private ctx: EngineContext,
    private orbitPivot: OrbitPivotManager
  ) {}

  /** Call after engine init to capture home view state and bind key listener */
  init(): void {
    this.homeViewState = {
      position: this.ctx.camera.position.clone(),
      target: this.ctx.controls.target.clone(),
      up: this.ctx.camera.up.clone(),
      zoom: this.ctx.camera.zoom
    };

    const shortcuts = this.ctx.session.keyboardShortcuts;

    this.handler = (event: KeyboardEvent) => {
      if (shortcuts.shouldSuppressShortcut(event)) return;

      const key = event.key.toUpperCase();

      if (shortcuts.isKey('focus-selection', key)) {
        event.preventDefault();
        this.focusSelection();
      }
      if (shortcuts.isKey('home-view', key)) {
        event.preventDefault();
        this.resetHomeView();
      }
      if (shortcuts.isKey('fit-all', key)) {
        event.preventDefault();
        this.fitAll();
      }
      if (shortcuts.isKey('toggle-grid', key)) {
        event.preventDefault();
        (this.ctx as { toggleGrid?(): void }).toggleGrid?.();
      }
      if (shortcuts.isKey('toggle-dimensions', key)) {
        event.preventDefault();
        this.ctx.toggleDimensionsHelper();
      }
    };

    window.addEventListener('keydown', this.handler);
  }

  /** Focus camera on selected objects, or fit all if nothing selected */
  focusSelection(): void {
    this.orbitPivot.clear();

    if (this.ctx.selectedObjects.length > 0) {
      const combinedBox = new Box3();
      for (const obj of this.ctx.selectedObjects) {
        combinedBox.union(new Box3().setFromObject(obj));
      }
      this.ctx.viewHelper.fitCameraToObject(combinedBox, 1);
    } else {
      this.ctx.viewHelper.fitCameraToObject(this.ctx.group, 1);
    }
  }

  /** Fit all objects in view (regardless of selection) */
  fitAll(): void {
    this.orbitPivot.clear();

    const box = contentBounds(this.ctx);
    if (box.isEmpty()) {
      this.ctx.viewHelper.fitCameraToObject(this.ctx.group, 1);
    } else {
      this.ctx.viewHelper.fitCameraToObject(box, 1);
    }
  }

  /** Reset camera to the initial home view position */
  resetHomeView(): void {
    if (!this.homeViewState) return;
    this.orbitPivot.clear();

    const { position, target, up, zoom } = this.homeViewState;

    new TWEEN.Tween(this.ctx.camera.position)
      .to({ x: position.x, y: position.y, z: position.z }, 280)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(() => {
        this.ctx.camera.lookAt(this.ctx.controls.target);
        this.ctx.controls.update();
      })
      .start();

    new TWEEN.Tween(this.ctx.controls.target)
      .to({ x: target.x, y: target.y, z: target.z }, 280)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(() => this.ctx.controls.update())
      .start();

    new TWEEN.Tween(this.ctx.camera.up)
      .to({ x: up.x, y: up.y, z: up.z }, 280)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(() => this.ctx.camera.lookAt(this.ctx.controls.target))
      .start();

    new TWEEN.Tween(this.ctx.camera)
      .to({ zoom: zoom }, 280)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(() => this.ctx.camera.updateProjectionMatrix())
      .start();
  }

  /** Clean up keyboard shortcuts */
  dispose(): void {
    if (this.handler) {
      window.removeEventListener('keydown', this.handler);
      this.handler = undefined;
    }
  }
}
